import {Component, computed, inject, input} from '@angular/core';
import {AdminProviderPhaseStatus, TenderPhaseType} from '@frontend/shared-api';
import {AppDatePipe} from '@frontend/shared';
import {NgClass} from '@angular/common';
import {TableModule} from 'primeng/table';
import {TooltipModule} from 'primeng/tooltip';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {AppPanelComponent} from '../../../../../shared/components/header/panel/app-panel.component';
import {getProviderPhaseColor} from '../../../../../shared/constants/provider-phase.constants';
import {
  ProviderStatusVM,
  PHASE_PLACEHOLDER,
  PROVIDER_STATUS_TOOLTIP_KEY_PREFIX,
  SUBMISSION_DATE_RED_DAYS,
  SUBMISSION_DATE_ORANGE_DAYS,
  SUBMISSION_DATE_GREEN_DAYS,
} from './provider-status-table.types';
import {ProviderStatusLookupService} from './provider-status-lookup.service';
import {isSubmissionDateStatus} from '../project-overview.utils';
import {addDays} from '../../../../../shared/utils/date-utils';

@Component({
  selector: 'app-provider-status-table',
  templateUrl: './provider-status-table.component.html',
  imports: [
    NgClass,
    TableModule,
    TooltipModule,
    TranslocoPipe,
    LoadingComponent,
    AppPanelComponent,
  ],
  providers: [AppDatePipe],
})
export class ProviderStatusTableComponent {
  private readonly providerStatusLookup = inject(ProviderStatusLookupService);
  private readonly transloco = inject(TranslocoService);
  private readonly appDatePipe = inject(AppDatePipe);

  phase = input.required<TenderPhaseType>();
  providerStatuses = input.required<AdminProviderPhaseStatus[]>();

  protected readonly rows = computed<ProviderStatusVM[]>(() =>
    this.providerStatuses().map((providerStatus: AdminProviderPhaseStatus) => ({
      providerName: this.getProviderName(providerStatus.hrToolDirectusUuid),
      status: providerStatus.status,
      submissionDate: providerStatus.submissionDate
        ? new Date(providerStatus.submissionDate)
        : undefined,
      formattedSubmissionDate: this.appDatePipe.transform(providerStatus.submissionDate),
    })),
  );

  protected readonly isLoading = this.providerStatusLookup.isLoading;

  protected getStatusColor(status: string): string {
    return getProviderPhaseColor(status);
  }

  protected getStatusTooltip(row: ProviderStatusVM): string {
    const statusHelpText = this.providerStatusLookup
      .statusTooltipTextsByKey()
      .get(PROVIDER_STATUS_TOOLTIP_KEY_PREFIX + row.status)
      ?.replaceAll(PHASE_PLACEHOLDER, this.phase());
    const submissionDateText = this.showsSubmissionDate(row)
      ? `${this.transloco.translate('provider.tenders.submissionDate')}: ${row.formattedSubmissionDate}`
      : undefined;
    return [statusHelpText, submissionDateText].filter(Boolean).join('<br><br>');
  }

  protected getSubmissionDateColorClass(row: ProviderStatusVM): string {
    const submissionDate = row.submissionDate;
    if (!submissionDate || !isSubmissionDateStatus(row.status)) return '';
    const today = new Date();
    if (submissionDate < addDays(today, SUBMISSION_DATE_RED_DAYS)) return 'text-red';
    if (submissionDate < addDays(today, SUBMISSION_DATE_ORANGE_DAYS)) return 'text-orange';
    if (submissionDate < addDays(today, SUBMISSION_DATE_GREEN_DAYS)) return 'text-green';
    return '';
  }

  private showsSubmissionDate(row: ProviderStatusVM): boolean {
    return !!row.submissionDate && isSubmissionDateStatus(row.status);
  }

  private getProviderName(hrToolDirectusUuid: string): string {
    return (
      this.providerStatusLookup.providerNamesByUuid().get(hrToolDirectusUuid) ?? hrToolDirectusUuid
    );
  }
}
